import { useEffect, useState } from 'react';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import { wazuhAPI } from 'src/api/mindtime/wazuhAPI';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import type {
  WazuhAgentOverviewData,
  WazuhEndpointActionRequiredItem,
  WazuhMostVulnerableEndpointItem,
  WazuhOrganizationAttentionItem,
  WazuhPagedData,
  WazuhPortfolioHealthResellerData,
  WazuhPortfolioHealthSubOrgData,
  WazuhRecentIncidentItem,
  WazuhScopeParams,
  WazuhSecurityScoreData,
} from 'src/types/mindtime/wazuhDashboard';
import logger from 'src/utils/logger';
import { applyProfileResellerScope } from './wazuhScopeParams';

type WazuhOverviewCommonState = {
  loading: boolean;
  agentOverview: WazuhAgentOverviewData | null;
  securityScore: WazuhSecurityScoreData | null;
  organizationsAttention: WazuhPagedData<WazuhOrganizationAttentionItem> | null;
  endpointsActionRequired: WazuhPagedData<WazuhEndpointActionRequiredItem> | null;
  recentIncidents: WazuhPagedData<WazuhRecentIncidentItem> | null;
};

export type WazuhResellerOverviewState = WazuhOverviewCommonState & {
  portfolioHealth: WazuhPortfolioHealthResellerData | null;
};

export type WazuhSubOrgOverviewState = WazuhOverviewCommonState & {
  portfolioHealth: WazuhPortfolioHealthSubOrgData | null;
  mostVulnerableEndpoints: WazuhPagedData<WazuhMostVulnerableEndpointItem> | null;
};

const emptyCommonState = (): WazuhOverviewCommonState => ({
  loading: true,
  agentOverview: null,
  securityScore: null,
  organizationsAttention: null,
  endpointsActionRequired: null,
  recentIncidents: null,
});

export type OverviewListPages = {
  attentionPage?: number;
  endpointsPage?: number;
  incidentsPage?: number;
};

const fetchOverviewCommonData = (
  scope: WazuhScopeParams,
  pageSize: number,
  pages: OverviewListPages = {}
): Promise<
  [
    WazuhAgentOverviewData,
    WazuhSecurityScoreData,
    WazuhPagedData<WazuhOrganizationAttentionItem>,
    WazuhPagedData<WazuhEndpointActionRequiredItem>,
    WazuhPagedData<WazuhRecentIncidentItem>,
  ]
> =>
  Promise.all([
    wazuhAPI.dashboard.getAgentOverview(scope),
    wazuhAPI.dashboard.getSecurityScore(scope),
    wazuhAPI.dashboard.getOrganizationsAttention({
      ...scope,
      page: pages.attentionPage ?? 0,
      pageSize,
    }),
    wazuhAPI.dashboard.getEndpointsActionRequired({
      ...scope,
      page: pages.endpointsPage ?? 0,
      pageSize,
    }),
    wazuhAPI.dashboard.getRecentIncidents({
      ...scope,
      page: pages.incidentsPage ?? 0,
      pageSize,
    }),
  ]);

export const useWazuhResellerOverviewData = (
  pageSize = 5,
  pages: OverviewListPages = {}
): WazuhResellerOverviewState => {
  const { scope } = useWazuhScope();
  const { userData } = useUserProfile();
  const profileOrgId = userData?.organization?.id;
  const attentionPage = pages.attentionPage ?? 0;
  const endpointsPage = pages.endpointsPage ?? 0;
  const incidentsPage = pages.incidentsPage ?? 0;
  const [state, setState] = useState<WazuhResellerOverviewState>({
    ...emptyCommonState(),
    portfolioHealth: null,
  });

  useEffect(() => {
    let cancelled = false;

    if (profileOrgId) {
      setState((prev) => ({ ...prev, loading: true }));

      const scopeWithReseller = applyProfileResellerScope(scope, profileOrgId);

      Promise.all([
        wazuhAPI.dashboard.getPortfolioHealthReseller(scopeWithReseller),
        fetchOverviewCommonData(scopeWithReseller, pageSize, {
          attentionPage,
          endpointsPage,
          incidentsPage,
        }),
      ])
        .then(([portfolioHealth, [agentOverview, securityScore, organizationsAttention, endpointsActionRequired, recentIncidents]]) => {
          if (cancelled) return;
          setState({
            loading: false,
            portfolioHealth,
            agentOverview,
            securityScore,
            organizationsAttention,
            endpointsActionRequired,
            recentIncidents,
          });
        })
        .catch((error) => {
          if (!cancelled) {
            logger(error, 'error', 'useWazuhResellerOverviewData');
            setState({ ...emptyCommonState(), loading: false, portfolioHealth: null });
          }
        });
    }

    return () => {
      cancelled = true;
    };
  }, [scope, profileOrgId, pageSize, attentionPage, endpointsPage, incidentsPage]);

  return state;
};

export const useWazuhSubOrgOverviewData = (pageSize = 5): WazuhSubOrgOverviewState => {
  const { scope } = useWazuhScope();
  const [state, setState] = useState<WazuhSubOrgOverviewState>({
    ...emptyCommonState(),
    portfolioHealth: null,
    mostVulnerableEndpoints: null,
  });

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true }));

    Promise.all([
      wazuhAPI.dashboard.getPortfolioHealthSubOrg(scope),
      fetchOverviewCommonData(scope, pageSize),
      wazuhAPI.dashboard.getMostVulnerableEndpoints({ ...scope, page: 0, pageSize }),
    ])
      .then(([
        portfolioHealth,
        [agentOverview, securityScore, organizationsAttention, endpointsActionRequired, recentIncidents],
        mostVulnerableEndpoints,
      ]) => {
        if (cancelled) return;
        setState({
          loading: false,
          portfolioHealth,
          agentOverview,
          securityScore,
          organizationsAttention,
          endpointsActionRequired,
          recentIncidents,
          mostVulnerableEndpoints,
        });
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'useWazuhSubOrgOverviewData');
          setState({
            ...emptyCommonState(),
            loading: false,
            portfolioHealth: null,
            mostVulnerableEndpoints: null,
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [scope, pageSize]);

  return state;
};

/** @deprecated Use useWazuhResellerOverviewData or useWazuhSubOrgOverviewData */
export const useWazuhOverviewData = useWazuhResellerOverviewData;
